import { useState } from 'react';
import { TrendingUp, TrendingDown, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const DAY = 86400000;
export const STALE_DAYS = 366;
export const THIN_COVERAGE_MAX = 4;
const TREND_MIN_SHIFT_PTS = 10;
const TREND_LOOKBACK = 3;

export function periodAgeDays(period, now = Date.now()) {
  if (!period) return null;
  const t = new Date(period.length === 10 ? `${period}T00:00:00Z` : period).getTime();
  return Number.isFinite(t) ? Math.floor((now - t) / DAY) : null;
}

export function isStaleCoverage(period, now = Date.now()) {
  const age = periodAgeDays(period, now);
  return age != null && age > STALE_DAYS;
}

function bullishShare(row) {
  const total = ['strong_buy', 'buy', 'hold', 'sell', 'strong_sell'].reduce((s, k) => s + Number(row?.[k] || 0), 0);
  if (!total) return null;
  return ((Number(row.strong_buy || 0) + Number(row.buy || 0)) / total) * 100;
}

// trends arrive most-recent first (Finnhub order)
export function coverageTrend(trends, latestPeriod, now = Date.now()) {
  if (!Array.isArray(trends) || trends.length < TREND_LOOKBACK + 1) return null;
  const age = periodAgeDays(latestPeriod, now);
  if (age == null || age > 92) return null;
  const nowShare = bullishShare(trends[0]);
  const thenShare = bullishShare(trends[TREND_LOOKBACK]);
  if (nowShare == null || thenShare == null) return null;
  const shift = nowShare - thenShare;
  if (Math.abs(shift) < TREND_MIN_SHIFT_PTS) return null;
  return { dir: shift > 0 ? 'up' : 'down', shift, nowShare, thenShare, fromPeriod: trends[TREND_LOOKBACK].period, toPeriod: trends[0].period };
}

function fmtMonth(p) {
  if (!p) return '';
  const d = new Date(p.length === 10 ? `${p}T00:00:00Z` : p);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function CoverageTrendBadge({ trend }) {
  const [open, setOpen] = useState(false);
  if (!trend) return null;
  const up = trend.dir === 'up';
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 ring-emerald-500/40'
    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 ring-rose-500/40';
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 hover:brightness-110 transition-all ${cls}`}
        data-testid={`analyst-trend-badge-${trend.dir}`}
        title="Tap for how this is measured"
      >
        <Icon className="w-3 h-3" />
        {up ? 'Upgrading' : 'Downgrading'}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="analyst-trend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${up ? 'text-emerald-500' : 'text-rose-500'}`} />
              Analysts are {up ? 'upgrading' : 'downgrading'}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-foreground/85">
                <p>
                  The share of analysts rating this stock <strong>Buy or Strong Buy</strong> moved from{' '}
                  <span className="font-mono font-bold">{trend.thenShare.toFixed(0)}%</span> ({fmtMonth(trend.fromPeriod)}) to{' '}
                  <span className="font-mono font-bold">{trend.nowShare.toFixed(0)}%</span> ({fmtMonth(trend.toPeriod)}) —{' '}
                  <span className={`font-mono font-bold ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {trend.shift > 0 ? '+' : ''}{trend.shift.toFixed(0)} pts
                  </span>{' '}
                  over three months.
                </p>
                <p className="text-xs text-muted-foreground">
                  Badge appears when the shift is at least {TREND_MIN_SHIFT_PTS} points and the latest data is within three months. The direction of change is usually more useful than today&rsquo;s label — analysts are often late to downgrades.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ThinCoverageBadge({ count }) {
  const [open, setOpen] = useState(false);
  if (count == null || count <= 0 || count > THIN_COVERAGE_MAX) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 ring-1 ring-amber-500/40 hover:brightness-110 transition-all"
        data-testid="analyst-thin-coverage-badge"
        title="Tap for why this matters"
      >
        <Users className="w-3 h-3" />
        Thin coverage
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="analyst-thin-coverage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-500" />
              Only {count} analyst{count === 1 ? '' : 's'} cover this stock
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-foreground/85">
                <p>
                  With fewer than five analysts, the consensus label and the Low / Mean / High targets rest on a handful of opinions — often from regional or boutique brokers, sometimes ones that also helped the company raise money. One analyst changing their mind can flip the whole label.
                </p>
                <p className="text-xs text-muted-foreground">
                  Thin coverage also means less efficient pricing: fewer eyes on the filings, so both mispricings and surprises are more common. Treat the rating as a weak signal and lean on your own checks.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
